import React, { useCallback, useEffect, useId, useState } from 'react';
import { AlertTriangle, CheckCircle, FileText, Loader2, ShieldCheck, X } from 'lucide-react';
import {
  ConsentDocument,
  ConsentGroup,
  ConsentLog,
  consentService,
  describeConsentAction,
  describeConsentDevice,
} from '../services/consentService';
import { ApiError } from '../services/apiClient';
import { useToast } from '../contexts/ToastContext';
import { ConfirmDialog } from './ConfirmDialog';
import { useCloseWatcher } from '../hooks/useCloseWatcher';

/**
 * INA-TALK 이용 고지·동의 창 (2026-11, 예전 ChatDisclaimerModal을 대체).
 *
 * 문서는 서버가 소유한다(server/config/consent/chat_notice.json → GET /api/consent/chat-notice). 여기서는
 * **텍스트로만** 그린다 — HTML을 넣지 않는다(dangerouslySetInnerHTML 금지).
 *
 * 화면에서 받는 것은 문서의 체크 항목(고지 확인·이용 수칙 확인) 두 개이고, 대화 내용 열람 동의는
 * '동의 후 시작'을 누르는 것으로 받는다 — 고지문 '대화 내용 열람' 절이 그 뜻을 적는다(2026-11-05 사용자 결정으로
 * 열람 동의/거부 라디오를 없앴다. 그 전 v3는 라디오로 따로 받았다). 기록은 예전과 같은 모양(audit_view 포함)이다.
 * 거부는 '동의하지 않음' 버튼 하나다. '전체 동의' 체크는 두지 않는다.
 * ⚠ choice 항목은 그리지 않는다 — 서버가 구버전(2.1.6 이하) 앱 호환용으로 열람 라디오를 응답에 붙인다
 *   (consentDocs.itemsForClients).
 *
 * mode
 *  - 'required': 닫을 수 없다(오버레이·ESC·X 없음). 서버가 required라고 판정했을 때만 App이 연다.
 *  - 'review'  : 설정의 '이용 고지·동의'. 닫을 수 있고, 내 동의 내역과 '동의 철회'가 있다.
 */
interface ConsentModalProps {
  isOpen: boolean;
  mode: 'required' | 'review';
  /** required일 때 서버 판정 사유(none·changed·expired·declined·withdrawn) — 안내 문구만 바꾼다 */
  reason?: string;
  /** 동의 기록 성공 */
  onAccepted: () => void;
  /** 동의하지 않음(기록 시도 후) — App이 로그아웃한다 */
  onDeclined: (contact: string) => void;
  /** 동의 철회 기록 성공 — App이 로그아웃한다 */
  onWithdrawn: () => void;
  /** 문서를 못 불러온 채 빠져나갈 길(required) — App이 로그아웃한다 */
  onLogout?: () => void;
  /** review에서 닫기 */
  onClose?: () => void;
}

const GROUP_STYLE: Record<string, { icon: React.ElementType; iconClass: string; badge: string }> = {
  notice: { icon: FileText, iconClass: 'text-blue-600', badge: '고지' },
  rules: { icon: AlertTriangle, iconClass: 'text-amber-600', badge: '이용 수칙' },
  consent: { icon: ShieldCheck, iconClass: 'text-emerald-600', badge: '별도 동의' },
};

function reasonMessage(reason: string | undefined, reagreeDays: number): string {
  switch (reason) {
    case 'changed':
      return '이용 고지 내용이 바뀌었습니다. 다시 확인하고 동의해 주세요.';
    case 'expired':
      return `마지막으로 동의한 지 ${reagreeDays}일이 지나 다시 확인을 받습니다.`;
    case 'declined':
    case 'withdrawn':
      return '동의하지 않은 상태입니다. INA-TALK을 이용하려면 아래 항목에 동의해 주세요.';
    default:
      return 'INA-TALK을 이용하려면 아래 고지를 읽고 동의해 주세요.';
  }
}

function formatDateTime(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function itemsSummary(log: ConsentLog): string {
  if (log.action === 'withdraw') return '모든 항목 철회';
  const i = log.items || {};
  const yn = (v: boolean | undefined) => (v ? '동의' : '거부');
  return `고지 확인 ${yn(i.notice)} · 수칙 ${yn(i.rules)} · 대화 열람 ${yn(i.audit_view)}`;
}

const GroupBlock: React.FC<{ group: ConsentGroup; index: number }> = ({ group, index }) => {
  const style = GROUP_STYLE[group.key] || GROUP_STYLE.notice;
  const Icon = style.icon;
  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <h3 className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">
        <Icon size={18} className={`${style.iconClass} shrink-0`} aria-hidden="true" />
        <span className="flex-1">{index + 1}. {group.title}</span>
        <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{style.badge}</span>
      </h3>
      <div className="px-4 py-3 space-y-3">
        {group.sections.map((s, si) => (
          <div key={si}>
            <h4 className="text-[13px] font-semibold text-slate-700 mb-1">{s.title}</h4>
            {s.bullets.length > 0 && (
              <ul className="list-disc pl-5 space-y-1 text-xs text-slate-600 leading-relaxed">
                {s.bullets.map((b, bi) => <li key={bi}>{b}</li>)}
              </ul>
            )}
            {s.paragraphs.map((p, pi) => (
              <p key={pi} className="mt-1 text-xs text-slate-600 leading-relaxed">{p}</p>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

export const ConsentModal: React.FC<ConsentModalProps> = ({
  isOpen, mode, reason, onAccepted, onDeclined, onWithdrawn, onLogout, onClose,
}) => {
  const titleId = useId();
  const { showToast } = useToast();
  const isReview = mode === 'review';

  const [doc, setDoc] = useState<ConsentDocument | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<'decline' | 'withdraw' | null>(null);
  const [logs, setLogs] = useState<ConsentLog[] | null>(null);
  const [logsError, setLogsError] = useState(false);

  const resetChoices = () => setChecked({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setDoc(await consentService.getDocument());
    } catch (err) {
      setDoc(null);
      setLoadError(err instanceof ApiError && err.code === 'CONSENT_UNAVAILABLE'
        ? '이용 고지가 아직 게시되지 않았습니다. 관리자에게 문의해 주세요.'
        : '이용 고지를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 열 때마다 새로 받는다(문구·보관 기간이 바뀌었을 수 있다). 닫히면 선택을 비운다
  useEffect(() => {
    if (!isOpen) {
      resetChoices();
      setConfirm(null);
      setLogs(null);
      setLogsError(false);
      return;
    }
    void load();
    if (isReview) {
      consentService.myLogs().then(setLogs).catch(() => setLogsError(true));
    }
  }, [isOpen, isReview, load]);

  // 스크롤 잠금
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // 안드로이드 뒤로가기 — 재열람에서만(동의 창은 닫을 수 없다). 확인창이 떠 있으면 확인창(Modal)이 받는다
  const noopClose = useCallback(() => {}, []);
  useCloseWatcher(isOpen && isReview && !!onClose && !confirm, onClose || noopClose);

  // ESC — 재열람에서만, 확인창이 떠 있으면 확인창이 받는다
  useEffect(() => {
    if (!isOpen || !isReview || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !confirm) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isReview, onClose, confirm]);

  if (!isOpen) return null;

  const checkItems = doc ? doc.items.filter((i) => i.kind === 'check') : [];
  const canAccept = !!doc && checkItems.length > 0 && checkItems.every((i) => checked[i.key]) && !saving;
  const reagreeDays = doc?.reagreeDays ?? 100;
  const lastAccept = logs?.find((l) => l.action === 'accept') || null;

  const submitAccept = async () => {
    if (!doc || !canAccept) return;
    setSaving(true);
    try {
      await consentService.submit(doc, 'accept', { notice: true, rules: true, audit_view: true });
      onAccepted();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CONSENT_CHANGED') {
        showToast({ type: 'info', message: '고지 내용이 바뀌었습니다. 다시 확인해 주세요.' });
        resetChoices();
        await load();
      } else if (!(err instanceof ApiError && err.status === 401)) {
        showToast({ type: 'error', message: '동의를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const submitDecline = async () => {
    setConfirm(null);
    setSaving(true);
    try {
      if (doc) await consentService.submit(doc, 'decline', { notice: !!checked.notice, rules: !!checked.rules, audit_view: false });
    } catch {
      // 기록에 실패해도 사용자의 뜻(이용하지 않음)은 따른다 — 로그아웃은 진행
    } finally {
      setSaving(false);
    }
    onDeclined(doc?.contact || '');
  };

  const submitWithdraw = async () => {
    setConfirm(null);
    setSaving(true);
    try {
      await consentService.submit(doc || { documentId: 0, renderedHash: '' }, 'withdraw', {});
      onWithdrawn();
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        showToast({ type: 'error', message: '동의를 철회하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={isReview ? onClose : undefined}
        aria-hidden="true"
      />
      {/* 패널 높이는 dvh가 아니라 % — iOS 키보드에 맞춰 #root가 줄면 따라 줄어든다(Modal.tsx와 같은 규칙) */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-xl mx-4 max-h-[calc(100%-2rem)] flex flex-col bg-white rounded-2xl shadow-xl animate-modal-enter"
      >
        {/* 머리 */}
        <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
          <ShieldCheck size={24} className="text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <h2 id={titleId} className="text-lg font-semibold text-slate-800">
              {doc?.title || 'INA-TALK 이용 고지 및 동의'}
            </h2>
            {!isReview && (
              <p className="mt-0.5 text-xs text-slate-500">{reasonMessage(reason, reagreeDays)}</p>
            )}
          </div>
          {isReview && onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="닫기"
              className="p-2 -mr-2 -mt-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={20} aria-hidden="true" />
            </button>
          )}
        </div>

        {/* 본문 */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50">
          {loading && !doc && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              불러오는 중…
            </div>
          )}
          {loadError && !doc && (
            <div className="py-8 text-center">
              <p className="text-sm text-slate-600">{loadError}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="mt-3 px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg"
              >
                다시 시도
              </button>
            </div>
          )}
          {doc && doc.groups.map((g, i) => <GroupBlock key={g.key} group={g} index={i} />)}

          {isReview && (
            <section className="rounded-xl border border-slate-200 bg-white">
              <h3 className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">내 동의 내역</h3>
              <div className="px-4 py-3">
                {logsError && <p className="text-xs text-slate-500">동의 내역을 불러오지 못했습니다.</p>}
                {!logsError && logs === null && <p className="text-xs text-slate-500">불러오는 중…</p>}
                {logs && logs.length === 0 && (
                  <p className="text-xs text-slate-500">이 계정의 동의 기록이 없습니다</p>
                )}
                {logs && logs.length > 0 && (
                  <ul className="divide-y divide-slate-100">
                    {logs.slice(0, 20).map((l) => (
                      <li key={l.id} className="py-2 text-xs">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className={`font-medium ${l.action === 'accept' ? 'text-emerald-700' : 'text-brand-700'}`}>
                            {describeConsentAction(l.action)}
                          </span>
                          <span className="text-slate-500">{formatDateTime(l.createdAt)}</span>
                          {l.version != null && <span className="text-slate-400">고지 버전 {l.version}</span>}
                        </div>
                        <div className="mt-0.5 text-slate-500">
                          {itemsSummary(l)} · {describeConsentDevice(l.clientType, l.appVersion)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}
        </div>

        {/* 바닥 */}
        <div className="px-5 py-4 border-t border-slate-100 rounded-b-2xl bg-white">
          {!isReview && checkItems.length > 0 && (
            <div className="space-y-2 mb-3">
              {checkItems.map((it) => (
                <label key={it.key} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!checked[it.key]}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setChecked((prev) => ({ ...prev, [it.key]: on }));
                    }}
                    className="mt-0.5 h-4 w-4 accent-brand-600 shrink-0"
                  />
                  <span>{it.label}</span>
                </label>
              ))}
            </div>
          )}

          {doc && (
            <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
              {isReview && lastAccept
                ? `마지막 동의: ${formatDateTime(lastAccept.createdAt)}. `
                : ''}
              동의하면 일시·항목·접속 IP·기기 정보가 기록됩니다. {reagreeDays}일마다, 그리고 고지 내용이 바뀌면
              다시 확인을 받습니다. · 고지 버전 {doc.version} (시행 {doc.effectiveLabel || doc.effectiveDate})
            </p>
          )}

          {!isReview && (
            <div className="flex flex-col-reverse sm:flex-row gap-2">
              {!doc && onLogout && (
                <button
                  type="button"
                  onClick={onLogout}
                  className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-brand-600 text-brand-600 hover:bg-brand-50"
                >
                  로그아웃
                </button>
              )}
              {doc && (
                <>
                  <button
                    type="button"
                    onClick={() => setConfirm('decline')}
                    disabled={saving}
                    className="sm:w-auto px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 disabled:opacity-50"
                  >
                    동의하지 않음
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitAccept()}
                    disabled={!canAccept}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <CheckCircle size={18} aria-hidden="true" />}
                    {saving ? '저장 중…' : '동의 후 시작'}
                  </button>
                </>
              )}
            </div>
          )}

          {isReview && (
            <div className="flex flex-col-reverse sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => setConfirm('withdraw')}
                disabled={saving || (logs !== null && !lastAccept)}
                className="sm:w-auto px-4 py-2.5 text-sm font-medium rounded-lg bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                동의 철회
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700"
              >
                확인
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 확인창은 패널(애니메이션 transform) 밖 형제로 둔다 — 안에 두면 fixed 기준이 패널이 된다 */}
      <ConfirmDialog
        isOpen={confirm === 'decline'}
        title="동의하지 않음"
        message={'동의하지 않으면 INA-TALK을 이용할 수 없어 로그아웃됩니다.\n다시 로그인하면 언제든 다시 동의할 수 있습니다.'}
        confirmLabel="로그아웃"
        danger
        onConfirm={() => void submitDecline()}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        isOpen={confirm === 'withdraw'}
        title="동의 철회"
        message={'동의를 철회하면 모든 기기에서 로그아웃되고, 다시 동의해야 INA-TALK을 이용할 수 있습니다.\n철회할까요?'}
        confirmLabel="철회"
        danger
        onConfirm={() => void submitWithdraw()}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};
